import React from "react";
import type { PeopleSource } from "@playbookai/types";
import type { PeopleFieldResolveState } from "./people-field-chip.js";

const IDLE: PeopleFieldResolveState = { status: "idle" };
const PENDING: PeopleFieldResolveState = { status: "pending" };

/**
 * Asks `source.resolve` about ONE id, lazily: only while `id` is set, which
 * the caller does only for a person the roster does not hold. `idle` when
 * there is nothing to ask (no id, or a source with no `resolve`).
 */
export function useResolvedPerson(
  source: PeopleSource,
  id: string | null,
): PeopleFieldResolveState {
  const [answer, setAnswer] = React.useState<{
    readonly id: string;
    readonly state: PeopleFieldResolveState;
  } | null>(null);

  React.useEffect(() => {
    if (!id || !source.resolve) return;
    let live = true;
    source.resolve([id]).then(
      (people) => {
        if (!live) return;
        const person = people.find((entry) => entry.id === id) ?? null;
        setAnswer({ id, state: { status: "done", person } });
      },
      () => {
        if (live) setAnswer({ id, state: { status: "failed" } });
      },
    );
    return () => {
      live = false;
    };
  }, [id, source]);

  if (!id || !source.resolve) return IDLE;
  // An answer about another id is not an answer about this one.
  return answer?.id === id ? answer.state : PENDING;
}
